import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  librariesApi,
  librarySchedulesApi,
  queueApi,
  scheduleApi,
  workspacesApi,
} from '@/api/endpoints';
import { useWorkspace } from './useAuth';
import { queryKeys } from './queryKeys';
import type {
  CreateLibraryInput,
  UpsertLibraryScheduleInput,
  UpdateLibraryInput,
} from '@/types/api';

/**
 * The full workspace record, for its timezone.
 *
 * `WorkspaceContext` only holds `WorkspaceSummary`, which has no `timezone` —
 * so a screen that renders a posting time has to ask for it. Cached long: it
 * changes about once in a workspace's life.
 */
export function useCurrentWorkspace() {
  const { activeId } = useWorkspace();
  return useQuery({
    queryKey: ['workspaces', 'current', activeId ?? ''],
    queryFn: () => workspacesApi.current(),
    staleTime: 10 * 60_000,
  });
}

/* ─── Libraries ────────────────────────────────────────────────────────────*/

export function useLibraryList(params: { includeInactive?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.libraries.list(params),
    queryFn: () => librariesApi.list(params),
    placeholderData: (previous) => previous,
  });
}

export function useLibrary(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.libraries.detail(id ?? ''),
    queryFn: () => librariesApi.getById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Every library write invalidates the whole domain by prefix.
 *
 * Reordering and membership both come back as the full detail payload, so the
 * detail cache is seeded directly rather than refetched — the list still needs
 * invalidating for its counts.
 */
function useLibraryMutation<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>,
  detailId?: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.libraries.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.librarySchedules.all,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all });
      // A new member does not change the queue until the filler runs, but the
      // post count and the runway it drives are stale immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.queue.all });
      if (
        detailId &&
        result &&
        typeof result === 'object' &&
        'posts' in result
      ) {
        queryClient.setQueryData(queryKeys.libraries.detail(detailId), result);
      }
    },
  });
}

export function useCreateLibrary() {
  return useLibraryMutation((input: CreateLibraryInput) =>
    librariesApi.create(input)
  );
}

export function useUpdateLibrary() {
  return useLibraryMutation(
    ({ id, input }: { id: string; input: UpdateLibraryInput }) =>
      librariesApi.update(id, input)
  );
}

export function useDeleteLibrary() {
  return useLibraryMutation((id: string) => librariesApi.remove(id));
}

/**
 * Add posts to a library chosen at mutate time.
 *
 * `useAddLibraryPosts` binds its library when the hook is called, which suits
 * the library detail screen — it only ever has one. The composer picks the
 * library inside the publish modal, after the hook has already run, so it
 * needs the id on the call instead.
 */
export function useAddPostsToLibrary() {
  return useLibraryMutation(
    ({ libraryId, postIds }: { libraryId: string; postIds: string[] }) =>
      librariesApi.addPosts(libraryId, postIds)
  );
}

export function useAddLibraryPosts(libraryId: string) {
  return useLibraryMutation(
    (postIds: string[]) => librariesApi.addPosts(libraryId, postIds),
    libraryId
  );
}

export function useRemoveLibraryPost(libraryId: string) {
  return useLibraryMutation(
    (postId: string) => librariesApi.removePost(libraryId, postId),
    libraryId
  );
}

export function useReorderLibrary(libraryId: string) {
  return useLibraryMutation(
    (postIds: string[]) => librariesApi.reorder(libraryId, postIds),
    libraryId
  );
}

/* ─── Per-library schedules ────────────────────────────────────────────────*/

export function useLibrarySchedules(libraryId: string) {
  return useQuery({
    queryKey: queryKeys.librarySchedules.list(libraryId),
    queryFn: () => librarySchedulesApi.list(libraryId),
    enabled: Boolean(libraryId),
  });
}

function useScheduleMutation<TVars, TResult>(
  libraryId: string,
  mutationFn: (variables: TVars) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.librarySchedules.list(libraryId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.queue.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.libraries.all });
    },
  });
}

export function useUpsertLibrarySchedule(libraryId: string) {
  return useScheduleMutation(
    libraryId,
    ({
      socialAccountId,
      input,
    }: {
      socialAccountId: string;
      input: UpsertLibraryScheduleInput;
    }) => librarySchedulesApi.upsert(libraryId, socialAccountId, input)
  );
}

export function usePreviewLibrarySchedule(libraryId: string) {
  return useMutation({
    mutationFn: ({
      socialAccountId,
      input,
    }: {
      socialAccountId: string;
      input: UpsertLibraryScheduleInput;
    }) => librarySchedulesApi.preview(libraryId, socialAccountId, input),
  });
}

export function useSetLibraryScheduleActive(libraryId: string) {
  return useScheduleMutation(
    libraryId,
    ({
      socialAccountId,
      isActive,
    }: {
      socialAccountId: string;
      isActive: boolean;
    }) => librarySchedulesApi.setActive(libraryId, socialAccountId, isActive)
  );
}

export function useDeleteLibrarySchedule(libraryId: string) {
  return useScheduleMutation(libraryId, (socialAccountId: string) =>
    librarySchedulesApi.remove(libraryId, socialAccountId)
  );
}

/* ─── Queue ────────────────────────────────────────────────────────────────*/

/**
 * The next planned posts.
 *
 * Deliberately not polled. The house rule is to poll only while something is
 * genuinely in flight and stop — a queue changes on a schedule measured in
 * hours, so an interval here would be traffic with nothing to show for it.
 * Skip and reschedule invalidate instead.
 */
export function useUpcoming(params: {
  limit?: number;
  socialAccountId?: string;
  libraryId?: string;
}) {
  return useQuery({
    queryKey: queryKeys.queue.upcoming(params),
    queryFn: () => queueApi.upcoming(params),
    placeholderData: (previous) => previous,
  });
}

export function useScheduleEvents(params: {
  from: number;
  to: number;
  socialAccountId?: string;
  libraryId?: string;
  kind?: import('@/types/enums').UpcomingKind;
  includePast?: boolean;
}) {
  return useQuery({
    queryKey: queryKeys.schedule.events(params),
    queryFn: () => scheduleApi.events({ ...params, limit: 1000 }),
    placeholderData: (previous) => previous,
  });
}

export function useRescheduleScheduleEvent() {
  return useScheduleMutation(
    '',
    ({
      kind,
      id,
      scheduledAtUtc,
    }: {
      kind: import('@/types/enums').UpcomingKind;
      id: string;
      scheduledAtUtc: number;
    }) => scheduleApi.reschedule(kind, id, scheduledAtUtc)
  );
}

export function useCancelScheduleEvent() {
  return useScheduleMutation(
    '',
    ({
      kind,
      id,
    }: {
      kind: import('@/types/enums').UpcomingKind;
      id: string;
    }) => scheduleApi.cancel(kind, id)
  );
}

function useQueueMutation<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.queue.all });
    },
  });
}

export function useSkipQueueEntry() {
  return useQueueMutation((id: string) => queueApi.skip(id));
}

export function useRescheduleQueueEntry() {
  return useQueueMutation(
    ({ id, scheduledAtUtc }: { id: string; scheduledAtUtc: number }) =>
      queueApi.reschedule(id, scheduledAtUtc)
  );
}
